import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Play, Target, Brain, ArrowRight, Star, AlertTriangle,
  Calculator, BarChart2, FileText, Layers, PenTool, Lock,
  Youtube
} from 'lucide-react'
import { useProgressStore, useLearnProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'

// ── 12단계 학습 데이터 (완전 내장) ───────────────────────────────────────────
const STEPS = [
  {
    id: 'LS01',
    order: 1,
    title: '변수 이해',
    subtitle: '변하는 값과 고정된 값을 구별한다',
    difficulty: 'beginner' as const,
    teaches: ['Variable', 'Constant', 'Expression'],
    icon: '🔤',
    color: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', accent: 'text-blue-600', btn: 'bg-blue-500' },
    objective: '변수(변하는 값)와 상수(고정된 값)의 차이를 이해하고, 실생활에서 변수를 찾을 수 있다.',
    concept: `변수(Variable)란 상황에 따라 달라지는 값을 문자로 나타낸 것입니다.

📌 핵심 개념
• 변수: 값이 변하는 양 → 보통 x, y, t 등의 문자로 표현
• 상수: 항상 고정된 값 → 숫자 2, 5, -3 등
• 식(Expression): 변수와 상수로 이루어진 수학적 표현

예를 들어 "나이"는 해마다 바뀌므로 변수, "손가락 수 10"은 변하지 않으므로 상수입니다.
1차 함수 y = 2x + 1에서 x와 y는 변수이고, 2와 1은 상수입니다.`,
    examples: [
      { q: '다음 중 변수는?  ①한 달의 날수  ②버스 요금  ③원주율(π)', a: '② 버스 요금 — 노선에 따라 달라지므로 변수', detail: '한 달의 날수(28~31)도 바뀌므로 변수라 볼 수 있습니다. 원주율은 3.14...로 고정.' },
      { q: '편의점에서 과자 한 봉지에 1,500원. x봉지를 사면 총 금액 y원. 여기서 변수는?', a: 'x(봉지 수)와 y(총 금액) — 둘 다 변수!', detail: '1,500은 상수입니다. x와 y는 서로 연결된 변수입니다.' },
    ],
    keyPoints: ['변수는 문자로 표현한다', 'x와 y는 서로 대응된다', '함수는 변수 사이의 관계'],
    practice: { q: '자동차가 시속 v km로 달릴 때, 3시간 후 이동 거리 d km를 식으로 나타내세요.', a: 'd = 3v  (v가 변수)', hint: '거리 = 속력 × 시간',
      solutionSteps: [
        { label: '① 공식 확인', content: '거리 = 속력 × 시간' },
        { label: '② 변수 대입', content: '속력 = v km/h, 시간 = 3시간 → 거리 d = v × 3' },
        { label: '③ 최종 식', content: 'd = 3v  (v가 시속이라는 변수, 3은 상수)' },
      ]
    },
    misconceptions: [
      { code: 'M01', desc: '변수를 그냥 "모르는 숫자"로만 생각한다 → 변수는 여러 값을 가질 수 있는 양!' },
    ],
  },
  {
    id: 'LS02',
    order: 2,
    title: '순서쌍과 좌표평면',
    subtitle: '두 변수의 관계를 점으로 표현한다',
    difficulty: 'beginner' as const,
    teaches: ['Point', 'CoordinatePlane', 'OrderedPair'],
    icon: '📍',
    color: { bg: 'bg-indigo-50', border: 'border-indigo-200', badge: 'bg-indigo-100 text-indigo-700', accent: 'text-indigo-600', btn: 'bg-indigo-500' },
    objective: '순서쌍 (x, y)의 의미를 알고, 좌표평면 위에 점을 찍을 수 있다.',
    concept: `순서쌍은 두 수의 쌍 (x, y)으로, 좌표평면 위의 점 하나를 정확히 나타냅니다.

📌 핵심 개념
• 순서쌍 (a, b): x좌표 a, y좌표 b인 점
• x축: 가로 방향 (오른쪽 +, 왼쪽 -)
• y축: 세로 방향 (위쪽 +, 아래쪽 -)
• 원점 O: (0, 0) — 두 축이 만나는 점
• 사분면: 1사분면(+,+), 2사분면(-,+), 3사분면(-,-), 4사분면(+,-)

순서쌍에서 순서가 중요합니다. (2, 3)과 (3, 2)는 서로 다른 점입니다!`,
    examples: [
      { q: '점 A(3, -2)를 좌표평면에 나타낼 때 어느 사분면에 있나요?', a: '제4사분면 (x>0, y<0)', detail: 'x=3(양수)이므로 오른쪽, y=-2(음수)이므로 아래쪽 → 4사분면' },
      { q: '점 B(-1, 4)는 어느 사분면?', a: '제2사분면 (x<0, y>0)', detail: 'x=-1(음수)→왼쪽, y=4(양수)→위쪽 → 2사분면' },
    ],
    keyPoints: ['순서쌍에서 순서가 중요하다', 'x축은 가로, y축은 세로', '원점은 (0, 0)'],
    practice: { q: '다음 순서쌍을 좌표평면에 표시하고 연결하세요: (0,1), (1,3), (2,5), (3,7)', a: '기울기 2, y절편 1인 직선이 됩니다! → y = 2x + 1', hint: '점들이 직선 위에 있는지 확인해보세요.',
      solutionSteps: [
        { label: '① 순서쌍 읽기', content: '(x, y) 순서로: (0,1), (1,3), (2,5), (3,7)' },
        { label: '② x 변화당 y 변화', content: 'x: 0→1→2→3 (1씩 증가), y: 1→3→5→7 (2씩 증가)' },
        { label: '③ 기울기·y절편', content: '기울기 = 2 (x가 1 늘면 y가 2 증가), y절편 = 1 (x=0일 때 y=1)' },
        { label: '④ 함수식', content: 'y = 2x + 1' },
      ]
    },
    misconceptions: [
      { code: 'M02', desc: '(x, y)에서 x와 y의 위치를 바꿔서 읽는다 → 반드시 x가 먼저!' },
    ],
  },
  {
    id: 'LS03',
    order: 3,
    title: '표에서 함수 관계 찾기',
    subtitle: '표를 보고 x와 y의 대응 규칙을 발견한다',
    difficulty: 'beginner' as const,
    teaches: ['Function', 'Mapping', 'InputOutput'],
    icon: '📊',
    color: { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-600', btn: 'bg-emerald-500' },
    objective: '표에서 x와 y의 대응 관계를 파악하고, 함수인지 아닌지 판단할 수 있다.',
    concept: `함수란 x의 값 하나에 y의 값이 딱 하나 대응되는 관계입니다.

📌 핵심 개념
• 함수: x → y 로의 대응. x값 하나에 y값이 정확히 하나.
• 함수가 아닌 예: x=2일 때 y=3도 되고 y=5도 된다면 함수가 아님
• 표에서 함수 확인: x열에 같은 값이 있으면 y열도 같은 값이어야 함
• 대응 규칙: 표에서 y = (규칙) × x + (상수) 형태를 찾는다

함수는 "자판기"와 같습니다. 같은 버튼(x)을 누르면 항상 같은 음료(y)가 나옵니다.`,
    examples: [
      {
        q: '표를 보고 함수식을 찾으세요\nx: 1, 2, 3, 4\ny: 5, 8, 11, 14',
        a: 'y = 3x + 2',
        detail: 'x가 1 증가할 때 y가 3 증가 → 기울기 3. x=1일 때 y=5이면 5 = 3×1 + b → b=2'
      },
      {
        q: '다음은 함수인가요?\nx: 1, 2, 2, 3\ny: 4, 5, 6, 7',
        a: '함수가 아닙니다',
        detail: 'x=2일 때 y가 5이기도 하고 6이기도 함 → 함수 조건 위반!'
      },
    ],
    keyPoints: ['x 하나에 y 하나 → 함수', '표에서 변화율(Δy/Δx)이 일정하면 1차 함수', '규칙을 y = ax + b로 표현'],
    practice: { q: '표에서 함수식을 구하세요\nx: 0, 1, 2, 3 / y: -2, 1, 4, 7', a: 'y = 3x - 2', hint: 'x=0일 때 y=-2 → y절편=-2. 변화율=3',
      solutionSteps: [
        { label: '① y절편 읽기', content: 'x=0일 때 y=-2 → y절편 b = -2' },
        { label: '② 변화율(기울기) 계산', content: 'Δy = 1-(-2) = 3, Δx = 1-0 = 1 → 기울기 a = 3' },
        { label: '③ 함수식 완성', content: 'y = 3x + (-2) = 3x - 2' },
        { label: '④ 검증', content: 'x=2: y=3(2)-2=4 ✓, x=3: y=3(3)-2=7 ✓' },
      ]
    },
    misconceptions: [
      { code: 'M06', desc: '표에서 x 변화량(Δx)을 보지 않고 y 변화량(Δy)만 본다 → Δy÷Δx 둘 다 확인!' },
    ],
  },
  {
    id: 'LS04',
    order: 4,
    title: '변화율 (율)',
    subtitle: 'x가 변할 때 y가 얼마나 변하는지 계산한다',
    difficulty: 'beginner' as const,
    teaches: ['RateOfChange', 'Slope', 'DeltaY', 'DeltaX'],
    icon: '📈',
    color: { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', accent: 'text-orange-600', btn: 'bg-orange-500' },
    objective: '변화율(Δy/Δx)의 개념을 이해하고, 두 점 사이의 변화율을 계산할 수 있다.',
    concept: `변화율이란 x가 1만큼 증가할 때 y가 얼마나 변하는지를 나타냅니다.

📌 핵심 개념
• 변화율 = Δy / Δx = (y의 변화량) / (x의 변화량)
• 1차 함수에서는 변화율이 항상 일정! (= 기울기)
• Δ(델타)는 "변화량"을 뜻하는 기호

변화율이 일정하다는 것은 "y = ax + b 형태"라는 뜻입니다.
일상 예: 시속 60km로 달리면, 1시간마다 60km씩 이동 → 변화율 = 60km/시간

📐 공식
변화율 = (y₂ - y₁) / (x₂ - x₁)`,
    examples: [
      { q: '두 점 (1, 3)과 (4, 9)를 지나는 직선의 변화율은?', a: '변화율 = (9-3)/(4-1) = 6/3 = 2', detail: 'x가 3 증가할 때 y가 6 증가 → 1 증가당 2 증가' },
      { q: '시간(분)과 물의 양(L) 표:\nx: 0, 2, 4, 6 / y: 10, 16, 22, 28\n변화율은?', a: '변화율 = (16-10)/(2-0) = 6/2 = 3 (L/분)', detail: '1분마다 물이 3L씩 증가' },
    ],
    keyPoints: ['변화율 = Δy ÷ Δx', '1차 함수에서 변화율은 일정', '변화율 = 기울기(a)'],
    practice: { q: '점 (-1, -1)과 (3, 7)을 지나는 직선의 변화율을 구하세요.', a: '변화율 = (7-(-1))/(3-(-1)) = 8/4 = 2', hint: 'Δy = 7-(-1), Δx = 3-(-1)',
      solutionSteps: [
        { label: '① Δy 계산', content: 'y의 변화량 = 7 - (-1) = 8' },
        { label: '② Δx 계산', content: 'x의 변화량 = 3 - (-1) = 4' },
        { label: '③ 변화율(기울기)', content: '변화율 = Δy/Δx = 8/4 = 2' },
      ]
    },
    misconceptions: [
      { code: 'M06', desc: '변화율을 Δx/Δy로 거꾸로 계산한다 → 분자=y변화량, 분모=x변화량. Δy÷Δx 순서!' },
    ],
  },
  {
    id: 'LS05',
    order: 5,
    title: '기울기(slope) 이해',
    subtitle: '기울기가 그래프의 모양을 결정한다',
    difficulty: 'intermediate' as const,
    teaches: ['Slope', 'LinearFunction', 'Direction'],
    icon: '📐',
    color: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-700', accent: 'text-yellow-600', btn: 'bg-yellow-500' },
    objective: '기울기(a)의 의미를 이해하고, 기울기의 크기와 부호에 따른 그래프 모양 변화를 설명할 수 있다.',
    concept: `기울기(slope)는 직선이 얼마나 가파른지, 어떤 방향인지를 나타냅니다.

📌 핵심 개념
• y = ax + b 에서 a가 기울기
• 기울기 a = Δy / Δx (변화율과 같음!)
• a > 0 : 오른쪽 위로 올라가는 직선 (증가)
• a < 0 : 오른쪽 아래로 내려가는 직선 (감소)
• a = 0 : 수평선 (함수가 아닌 상수함수)
• |a|가 클수록 더 가파름

📊 기울기 비교
• a = 3 : 급격히 증가  
• a = 1 : 45도 대각선  
• a = 0.5 : 완만하게 증가  
• a = -2 : 급격히 감소`,
    examples: [
      { q: 'y = 3x + 1 과 y = x + 1 중 어느 것이 더 가파른가요?', a: 'y = 3x + 1 (기울기 3 > 1)', detail: '기울기의 절댓값이 클수록 가파릅니다.' },
      { q: '두 점 (0, 2)와 (4, -2)를 지나는 직선의 기울기는?', a: 'a = (-2-2)/(4-0) = -4/4 = -1', detail: '음수 기울기 → 오른쪽으로 갈수록 내려가는 직선' },
    ],
    keyPoints: ['기울기 = 변화율 = a', '양수 기울기 → 증가, 음수 → 감소', '절댓값이 클수록 가파름'],
    practice: { q: '기울기가 -2이고 점 (1, 3)을 지나는 직선의 식을 구하세요.', a: 'y = -2x + 5', hint: '3 = -2×1 + b → b = 5',
      solutionSteps: [
        { label: '① 형태 설정', content: '기울기 -2 → y = -2x + b 로 놓기' },
        { label: '② b 계산', content: '점 (1,3) 대입: 3 = -2(1) + b → 3 = -2 + b → b = 5' },
        { label: '③ 함수식', content: 'y = -2x + 5' },
        { label: '④ 검증', content: 'x=1 대입: y = -2+5 = 3 ✓' },
      ]
    },
    misconceptions: [
      { code: 'M05', desc: '기울기가 음수이면 "함수가 아니다"라고 생각한다 → 음수 기울기도 완전한 1차 함수!' },
      { code: 'M01', desc: '기울기를 그냥 숫자로만 보고, 방향성(증가/감소)을 파악하지 않는다' },
    ],
  },
  {
    id: 'LS06',
    order: 6,
    title: 'y절편 이해',
    subtitle: 'y절편과 x절편 — 두 절편의 의미와 위치를 이해한다',
    difficulty: 'intermediate' as const,
    teaches: ['YIntercept', 'Constant', 'InitialValue'],
    icon: '🎯',
    color: { bg: 'bg-cyan-50', border: 'border-cyan-200', badge: 'bg-cyan-100 text-cyan-700', accent: 'text-cyan-600', btn: 'bg-cyan-500' },
    objective: 'y절편(b)의 의미를 이해하고, 그래프에서 y절편을 읽어낼 수 있다.',
    concept: `y절편이란 직선이 y축(x=0인 선)과 만나는 점의 y좌표입니다.

📌 핵심 개념
• y = ax + b 에서 b가 y절편
• x = 0 을 대입하면 y = b → y절편이 b임을 확인
• 그래프에서 직선이 y축과 만나는 점 = (0, b)
• 실생활에서 y절편 = "시작값", "기본값", "초기값"

📌 실생활 예시
• 택시 기본요금(3,800원) → y절편
• 저금통의 시작 금액(30,000원) → y절편  
• 초기 물 온도(20°C) → y절편

y절편이 양수이면 직선이 y축의 위쪽을 지나고,
y절편이 음수이면 아래쪽을 지납니다.`,
    examples: [
      { q: 'y = 4x - 3 에서 y절편은?', a: 'y절편 = -3 (점 (0, -3))', detail: 'x=0 대입: y = 4×0 - 3 = -3' },
      { q: '그래프가 (0, 5)를 지나고 기울기가 2이면 함수식은?', a: 'y = 2x + 5', detail: 'y절편 = 5, 기울기 = 2 → y = 2x + 5' },
    ],
    keyPoints: ['y절편 = x=0일 때 y값', '좌표 (0, b)로 나타냄', '실생활에서 초기값'],
    practice: { q: 'y = -x + 7 의 x절편과 y절편을 각각 구하세요.', a: 'y절편 = 7, x절편 = 7 (y=0 → x=7)', hint: 'y절편: x=0 대입 / x절편: y=0 대입',
      solutionSteps: [
        { label: '① y절편 (x=0 대입)', content: 'y = -(0) + 7 = 7 → y절편 = 7, 점 (0, 7)' },
        { label: '② x절편 (y=0 대입)', content: '0 = -x + 7 → x = 7 → x절편 = 7, 점 (7, 0)' },
        { label: '③ 결론', content: 'y절편 = 7, x절편 = 7 (이 경우 두 절편이 같음!)' },
      ]
    },
    misconceptions: [
      { code: 'M03', desc: 'y = 2x + 3에서 기울기가 3이고 y절편이 2라고 혼동한다 → ax+b에서 a=기울기, b=y절편!' },
    ],
  },
  {
    id: 'LS07',
    order: 7,
    title: '1차 함수식 y = ax + b',
    subtitle: '기울기와 y절편으로 함수식을 완성한다',
    difficulty: 'intermediate' as const,
    teaches: ['LinearFunction', 'Slope', 'YIntercept', 'Equation'],
    icon: '🧮',
    color: { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700', accent: 'text-violet-600', btn: 'bg-violet-500' },
    objective: '기울기와 y절편이 주어졌을 때 y = ax + b 형태의 함수식을 쓸 수 있다.',
    concept: `1차 함수의 기본 형태는 y = ax + b 입니다.

📌 함수식의 구조
• a : 기울기 (slope) — x계수, 변화율
• b : y절편 (y-intercept) — 상수항, 초기값
• x : 독립변수 (입력값)
• y : 종속변수 (출력값)

📌 함수식 구하는 방법

방법 1: 기울기와 y절편을 알 때
  → 바로 y = ax + b에 대입

방법 2: 기울기와 한 점을 알 때
  → y = ax + b에 점의 좌표를 대입하여 b 계산

방법 3: 두 점을 알 때
  → 기울기 = (y₂-y₁)/(x₂-x₁) 계산 후 방법 2 적용`,
    examples: [
      { q: '기울기 3, y절편 -2인 1차 함수식은?', a: 'y = 3x - 2', detail: 'a=3, b=-2를 y=ax+b에 바로 대입' },
      { q: '기울기가 -1이고 점 (2, 4)를 지나는 함수식은?', a: 'y = -x + 6', detail: '4 = -1×2 + b → b = 6 → y = -x + 6' },
      { q: '두 점 (1, 1)과 (3, 7)을 지나는 함수식은?', a: 'y = 3x - 2', detail: '기울기=(7-1)/(3-1)=3, 1=3×1+b → b=-2' },
    ],
    keyPoints: ['y = ax + b (a≠0)', '기울기와 한 점으로 b 결정', '두 점으로 a 먼저 계산'],
    practice: { q: '두 점 (-1, 5)와 (2, -1)을 지나는 1차 함수식을 구하세요.', a: 'y = -2x + 3', hint: '기울기=(-1-5)/(2-(-1))=-6/3=-2, 5=-2×(-1)+b → b=3',
      solutionSteps: [
        { label: '① 기울기 계산', content: 'a = (-1-5)/(2-(-1)) = -6/3 = -2' },
        { label: '② y절편 계산', content: 'y = -2x + b에 (-1, 5) 대입: 5 = -2(-1)+b = 2+b → b=3' },
        { label: '③ 함수식', content: 'y = -2x + 3' },
        { label: '④ 검증', content: '(2,-1) 대입: y = -2(2)+3 = -4+3 = -1 ✓' },
      ]
    },
    misconceptions: [
      { code: 'M03', desc: 'y = -2x + 3 에서 a = 3, b = -2 로 읽는 실수 → x 앞의 계수가 기울기!' },
    ],
  },
  {
    id: 'LS08',
    order: 8,
    title: '정비례 vs 1차 함수',
    subtitle: 'y = ax 와 y = ax + b 의 차이를 구별한다',
    difficulty: 'intermediate' as const,
    teaches: ['ProportionalFunction', 'LinearFunction', 'YIntercept'],
    icon: '⚖️',
    color: { bg: 'bg-teal-50', border: 'border-teal-200', badge: 'bg-teal-100 text-teal-700', accent: 'text-teal-600', btn: 'bg-teal-500' },
    objective: '정비례 함수(y=ax)와 1차 함수(y=ax+b)의 공통점과 차이점을 설명할 수 있다.',
    concept: `정비례 함수는 1차 함수의 특수한 경우입니다!

📌 비교표
┌────────────┬────────────┬─────────────────┐
│            │ 정비례     │ 1차 함수        │
├────────────┼────────────┼─────────────────┤
│ 식         │ y = ax     │ y = ax + b      │
│ y절편      │ 0 (원점)   │ b (임의값)      │
│ 그래프     │ 원점 통과  │ (0,b) 통과      │
│ x=0일 때   │ y = 0      │ y = b           │
└────────────┴────────────┴─────────────────┘

📌 핵심 포인트
• 정비례: b = 0 인 1차 함수 → 원점(0,0)을 반드시 통과
• 1차 함수: b ≠ 0 이면 원점을 통과하지 않음
• 모든 정비례 함수는 1차 함수, 하지만 1차 함수가 모두 정비례는 아님

실생활: 물건 한 개당 1,000원 → y = 1000x (정비례)
       기본료 2,000원 + 개당 1,000원 → y = 1000x + 2000 (1차 함수)`,
    examples: [
      { q: 'y = 3x 와 y = 3x + 2 의 차이점은?', a: 'y=3x는 원점(0,0)을 통과, y=3x+2는 (0,2)를 통과', detail: '기울기는 같지만 y절편이 다릅니다.' },
      { q: '원점을 지나고 기울기가 -4인 함수식은?', a: 'y = -4x  (정비례 함수)', detail: '원점 통과 → y절편=0 → b=0' },
    ],
    keyPoints: ['정비례: y=ax, 원점 통과', '1차함수: y=ax+b (b≠0)', '정비례 ⊂ 1차함수'],
    practice: { q: 'y = 5x 와 y = 5x - 3 의 그래프를 비교 설명하세요.', a: '같은 기울기(5)로 평행하지만, 위치가 다름. y=5x는 원점, y=5x-3은 (0,-3)을 통과', hint: '기울기가 같으면 두 직선은 평행합니다.',
      solutionSteps: [
        { label: '① 기울기 비교', content: 'y=5x의 기울기: 5 / y=5x-3의 기울기: 5 → 같음!' },
        { label: '② y절편 비교', content: 'y=5x의 y절편: 0 (원점) / y=5x-3의 y절편: -3' },
        { label: '③ 결론', content: '기울기 같음 → 평행, y절편 다름 → 서로 다른 직선\ny=5x는 원점(0,0), y=5x-3은 (0,-3)을 통과' },
      ]
    },
    misconceptions: [
      { code: 'M04', desc: '"y = 2x는 1차 함수가 아니다"라고 생각한다 → 정비례도 1차 함수의 일종!' },
    ],
  },
  {
    id: 'LS09',
    order: 9,
    title: '그래프 그리기',
    subtitle: '함수식에서 그래프를 직접 그린다',
    difficulty: 'intermediate' as const,
    teaches: ['Graph', 'XIntercept', 'YIntercept', 'Plotting'],
    icon: '📉',
    color: { bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-700', accent: 'text-rose-600', btn: 'bg-rose-500' },
    objective: '1차 함수의 그래프를 두 가지 방법(두 점 이용, 절편 이용)으로 그릴 수 있다.',
    concept: `직선을 그리려면 두 점만 있으면 됩니다!

📌 방법 1: y절편과 기울기 이용
① y절편(0, b)을 찍는다
② 기울기 a = (y의 변화)/(x의 변화)로 다음 점을 찾는다
③ 두 점을 연결하여 직선을 긋는다

예) y = 2x + 1
① (0, 1) 찍기
② 기울기 2 = 2/1 → x 1 증가, y 2 증가 → (1, 3) 찍기
③ 두 점을 잇는 직선 그리기

📌 방법 2: x절편과 y절편 이용
• x절편: y=0 대입 → x = -b/a → 점 (-b/a, 0)
• y절편: x=0 대입 → y = b → 점 (0, b)
• 두 점을 연결!`,
    examples: [
      { q: 'y = 2x - 4 의 그래프를 그리는 두 점을 구하세요.', a: 'x절편: (2, 0)  y절편: (0, -4)', detail: 'y=0: 2x-4=0 → x=2. x=0: y=-4' },
      { q: 'y = -x + 3 의 그래프 특징을 설명하세요.', a: '기울기 -1(오른쪽으로 갈수록 감소), y절편 3, x절편 3', detail: '오른쪽 아래 방향 직선. (0,3)과 (3,0)을 지남.' },
    ],
    keyPoints: ['직선은 두 점으로 결정', 'x절편: y=0 대입', 'y절편: x=0 대입'],
    practice: { q: 'y = -2x + 6 의 x절편, y절편을 구하고 그래프의 특징을 설명하세요.', a: 'x절편=3, y절편=6. 기울기 -2로 감소하는 직선. (3,0)과 (0,6)을 지남.', hint: 'x절편: y=0→-2x+6=0→x=3',
      solutionSteps: [
        { label: '① y절편 (x=0)', content: 'y = -2(0)+6 = 6 → y절편=6, 점(0,6)' },
        { label: '② x절편 (y=0)', content: '0 = -2x+6 → 2x=6 → x=3 → x절편=3, 점(3,0)' },
        { label: '③ 그래프 특징', content: '기울기 -2 < 0 → 오른쪽으로 갈수록 내려감 (감소)\n(3,0)과 (0,6)을 잇는 우하향 직선' },
      ]
    },
    misconceptions: [
      { code: 'M07', desc: '그래프에서 x절편과 y절편을 반대로 읽는다 → x절편은 x축과 만나는 점!' },
    ],
  },
  {
    id: 'LS10',
    order: 10,
    title: '그래프에서 식 구하기',
    subtitle: '그래프를 보고 함수식을 역으로 찾는다',
    difficulty: 'advanced' as const,
    teaches: ['LinearFunction', 'Slope', 'YIntercept', 'GraphReading'],
    icon: '🔍',
    color: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700', accent: 'text-purple-600', btn: 'bg-purple-500' },
    objective: '주어진 그래프에서 기울기와 y절편을 읽어 함수식을 구할 수 있다.',
    concept: `그래프 → 식으로 역변환하는 3단계 과정입니다.

📌 3단계 과정
① y절편 읽기: 그래프가 y축과 만나는 점의 y좌표 = b
② 기울기 계산: 그래프 위 두 점을 선택하여 Δy/Δx 계산 = a
③ 식 완성: y = ax + b 에 a와 b 대입

📌 기울기 읽는 팁
• 격자에서 "오른쪽으로 1칸, 위로 몇 칸?" = 기울기
• 오른쪽 아래 방향이면 음수 기울기
• 가로 격자 1칸 = x 1단위, 세로 격자 1칸 = y 1단위

⚠️ 주의: 그래프 격자의 눈금 간격을 반드시 확인하세요!
눈금 1칸이 2단위라면 기울기 계산도 그에 맞게!`,
    examples: [
      { q: '그래프가 (0, 3)과 (2, 7)을 지날 때 함수식은?', a: 'y = 2x + 3', detail: '기울기=(7-3)/(2-0)=2, y절편=3 → y=2x+3' },
      { q: '그래프가 x절편=-1, y절편=2를 가질 때 함수식은?', a: 'y = 2x + 2', detail: '점(-1,0)과 점(0,2). 기울기=(2-0)/(0-(-1))=2' },
    ],
    keyPoints: ['y축과의 교점 → y절편', '두 점으로 기울기 계산', '축척(눈금 단위) 확인'],
    practice: { q: '그래프가 점 (-2, 0)과 (0, -4)를 지나면 함수식은?', a: 'y = -2x - 4', hint: '기울기=(-4-0)/(0-(-2))=-4/2=-2, y절편=-4',
      solutionSteps: [
        { label: '① y절편 읽기', content: '(0, -4)를 지나므로 y절편 b = -4' },
        { label: '② 기울기 계산', content: 'a = (-4-0)/(0-(-2)) = -4/2 = -2' },
        { label: '③ 함수식', content: 'y = -2x + (-4) = -2x - 4' },
      ]
    },
    misconceptions: [
      { code: 'M08', desc: '그래프에서 기울기를 Δx/Δy로 거꾸로 계산한다 → 세로변화/가로변화 = Δy/Δx!' },
      { code: 'M07', desc: '눈금 단위를 1로 가정하고 읽다가 틀린다 → 반드시 축의 눈금 간격 확인!' },
    ],
  },
  {
    id: 'LS11',
    order: 11,
    title: '실생활 문장제 → 함수식',
    subtitle: '문장을 읽고 1차 함수식을 세운다',
    difficulty: 'advanced' as const,
    teaches: ['WordProblem', 'Modeling', 'LinearFunction', 'Application'],
    icon: '📝',
    color: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', accent: 'text-amber-600', btn: 'bg-amber-500' },
    objective: '실생활 문제를 읽고 변수를 정의한 뒤 1차 함수식으로 나타낼 수 있다.',
    concept: `문장제 풀이 4단계 전략입니다.

📌 4단계 전략
① 변수 설정: x = ?, y = ? 를 명확히 정의
② 기울기 찾기: "~마다", "~당", "~씩" 키워드 → 기울기(a)
③ y절편 찾기: "처음에", "기본", "시작" 키워드 → y절편(b)
④ 식 완성: y = ax + b 작성 및 검증

📌 키워드 매핑
• "1km당 500원 추가" → a = 500
• "기본요금 3,000원" → b = 3,000  
• "매주 5,000원씩 저금" → a = 5,000
• "현재 잔액 20,000원" → b = 20,000
• "1분마다 3°C 감소" → a = -3`,
    examples: [
      {
        q: '물탱크에 물이 200L 있다. 1분마다 15L씩 사용한다. x분 후 남은 물의 양 y는?',
        a: 'y = -15x + 200',
        detail: '기울기: -15 (감소), y절편: 200 (시작량)'
      },
      {
        q: '달리기 대회에서 출발 후 x초 동안 y미터를 달린다. 5초에 30m, 10초에 55m를 달렸다. 함수식은?',
        a: 'y = 5x + 5',
        detail: '기울기=(55-30)/(10-5)=5, 30=5×5+b → b=5'
      },
    ],
    keyPoints: ['변수 정의가 먼저!', '"~당", "~마다" → 기울기', '"처음", "기본" → y절편'],
    practice: { q: '핸드폰 배터리가 100%에서 시작. 1시간마다 8%씩 줄어든다. x시간 후 배터리 y%를 식으로 나타내고, 완전 방전되는 시간을 구하세요.', a: 'y = -8x + 100. 완전방전: 0 = -8x+100 → x = 12.5시간', hint: '감소하므로 기울기는 음수',
      solutionSteps: [
        { label: '① 변수 설정', content: 'x = 시간(h), y = 배터리(%)' },
        { label: '② 기울기', content: '"1시간마다 8%씩 감소" → a = -8 (음수!)' },
        { label: '③ y절편', content: '"처음 100%" → b = 100' },
        { label: '④ 함수식', content: 'y = -8x + 100' },
        { label: '⑤ 완전방전 시간', content: 'y=0 대입: 0 = -8x+100 → 8x=100 → x=12.5시간' },
      ]
    },
    misconceptions: [
      { code: 'M08', desc: '"1분마다 3 감소"를 a=3으로 쓴다 → 감소이므로 a=-3!' },
    ],
  },
  {
    id: 'LS12',
    order: 12,
    title: '오답 분석과 개념 정리',
    subtitle: '8가지 오개념을 진단하고 완전 학습한다',
    difficulty: 'advanced' as const,
    teaches: ['Misconception', 'Review', 'MasteryCheck'],
    icon: '🎓',
    color: { bg: 'bg-slate-50', border: 'border-slate-300', badge: 'bg-slate-100 text-slate-700', accent: 'text-slate-700', btn: 'bg-slate-600' },
    objective: '8가지 주요 오개념(M01~M08)을 스스로 진단하고, 올바른 개념으로 교정할 수 있다.',
    concept: `1차 함수를 배우면서 가장 자주 발생하는 8가지 오개념을 점검합니다.

📌 8가지 오개념 총정리

M01 기울기 오해
잘못: "기울기는 그냥 숫자" → 방향성과 변화율을 동시에 표현!

M02 x/y 역할 혼동
잘못: (3, 2)를 x=2, y=3으로 읽음 → (x좌표, y좌표) 순서!

M03 기울기/y절편 혼동
잘못: y = 2x + 3에서 기울기=3, y절편=2 → 기울기=2, y절편=3!

M04 정비례 = 1차 함수 혼동
잘못: "y=3x는 1차 함수가 아님" → 정비례도 1차 함수의 특수형!

M05 음수 기울기 오해
잘못: "기울기<0이면 함수가 아님" → 감소하는 1차 함수도 유효!

M06 표에서 변화율 오류
잘못: x 변화량을 보지 않고 y 변화량만 본다 → Δy÷Δx를 계산!

M07 그래프↔식 변환 오류
잘못: x절편과 y절편을 바꿔서 읽음 → x절편=x축 교점, y절편=y축 교점!

M08 문장제 방향 오류
잘못: "감소"를 양수 기울기로 씀 → 감소=음수 기울기(a<0)!`,
    examples: [
      { q: 'y = -3x + 6 에서 틀린 설명을 고르세요.\n① 기울기는 -3이다\n② y절편은 6이다\n③ x가 증가하면 y가 증가한다\n④ x절편은 2이다', a: '③이 틀렸습니다. 기울기가 음수이므로 x 증가 시 y 감소!', detail: 'M05 오개념: 음수 기울기 → 감소 함수' },
      { q: '표: x=1→y=7, x=2→y=10, x=4→y=16\n기울기는?', a: '기울기 = 3 (Δy/Δx = 3/1 = 3)', detail: 'M06 주의: Δx가 2인 구간도 Δy=6이므로 6/2=3으로 일정' },
    ],
    keyPoints: ['M01~M08 오개념 체크', 'AI 튜터로 개인별 진단', '완전 학습 후 마스터리 확인'],
    practice: { q: '종합 문제: 점 A(-1, 5)와 B(3, -3)을 지나는 1차 함수에서\n① 기울기, ② y절편, ③ x절편, ④ 함수식을 구하세요.', a: '① a = (-3-5)/(3-(-1)) = -2  ② b: 5 = -2×(-1)+b → b=3  ③ 0=-2x+3 → x=1.5  ④ y = -2x + 3', hint: '순서대로: 기울기→y절편→x절편→함수식',
      solutionSteps: [
        { label: '① 기울기 계산', content: 'a = (-3-5)/(3-(-1)) = -8/4 = -2' },
        { label: '② y절편 계산', content: 'y=-2x+b에 A(-1,5) 대입: 5=-2(-1)+b → 5=2+b → b=3' },
        { label: '③ x절편 계산', content: 'y=0: 0=-2x+3 → 2x=3 → x=1.5' },
        { label: '④ 함수식', content: 'y = -2x + 3' },
        { label: '⑤ 검증', content: 'B(3,-3): y=-2(3)+3=-6+3=-3 ✓' },
      ]
    },
    misconceptions: [
      { code: 'M01', desc: '기울기를 단순 수로만 인식' },
      { code: 'M02', desc: 'x/y 역할 혼동' },
      { code: 'M03', desc: '기울기와 y절편 위치 혼동' },
      { code: 'M04', desc: '정비례와 1차 함수 혼동' },
      { code: 'M05', desc: '음수 기울기 오해' },
      { code: 'M06', desc: '표에서 변화율 계산 오류' },
      { code: 'M07', desc: '그래프 해석 오류' },
      { code: 'M08', desc: '문장제 방향성 오류' },
    ],
  },
]

const DIFFICULTY_CONFIG = {
  beginner: { label: '초급', color: 'bg-emerald-100 text-emerald-700' },
  intermediate: { label: '중급', color: 'bg-orange-100 text-orange-700' },
  advanced: { label: '고급', color: 'bg-red-100 text-red-700' },
}

// ── 연습문제 컴포넌트 (단계별 풀이 + 진도 연동) ──────────────────────────────
function PracticeBox({
  practice, stepId, unitId, alreadyAnswered, onAnswered,
}: {
  practice: { q: string; a: string; hint: string; solutionSteps?: { label: string; content: string }[] }
  stepId: string
  unitId: string
  alreadyAnswered: boolean
  onAnswered: () => void
}) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(alreadyAnswered)
  const [showSteps, setShowSteps] = useState(false)
  const markPractice = useProgressStore(s => s.markPracticeAnswered)

  // 단계별 풀이: 직접 제공된 steps 우선, 없으면 generic fallback
  const solutionSteps = practice.solutionSteps ?? [
    { label: '① 핵심 공식 확인', content: practice.hint },
    { label: '② 풀이 과정', content: '위 공식/힌트를 이용해 단계적으로 계산합니다.' },
    { label: '③ 최종 답', content: practice.a },
  ]

  const handleShowAnswer = () => {
    setShowAnswer(true)
    if (!alreadyAnswered) {
      markPractice(unitId, stepId)
      onAnswered()
    }
  }

  return (
    <div className={`rounded-xl border-2 p-4 transition-all ${
      alreadyAnswered ? 'border-emerald-300 bg-emerald-50/60' : 'border-orange-200 bg-orange-50'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-orange-600 flex items-center gap-1.5">
          <Play size={12} /> 연습 문제
        </p>
        {alreadyAnswered && (
          <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
            <CheckCircle size={11} /> 풀이 완료
          </span>
        )}
      </div>
      <p className="text-sm text-slate-800 font-medium whitespace-pre-line mb-3">{practice.q}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors flex items-center gap-1"
        >
          <PenTool size={11} /> {showSteps ? '풀이 접기' : '단계별 풀이'}
        </button>
        <button
          onClick={handleShowAnswer}
          className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors flex items-center gap-1"
        >
          <CheckCircle size={11} /> {showAnswer ? '정답 확인 중' : '정답 보기'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
          💡 <span className="font-medium">{practice.hint}</span>
        </div>
      )}
      {showSteps && (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
          <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5">
            <PenTool size={11} /> 단계별 풀이 과정
          </p>
          <div className="space-y-2">
            {solutionSteps.map((step, i) => (
              <div key={i} className="flex gap-2">
                <div className="w-5 h-5 bg-blue-500 text-white rounded-full text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-slate-600 mb-0.5">{step.label}</p>
                  <div className="bg-white rounded-lg px-2 py-1.5 border border-blue-100">
                    <p className="text-xs text-slate-700 whitespace-pre-line">{step.content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 bg-emerald-50 border-2 border-emerald-300 rounded-xl px-3 py-2.5">
          <p className="text-xs font-bold text-emerald-600 mb-1">✅ 정답</p>
          <p className="text-sm font-bold text-emerald-800 whitespace-pre-line">{practice.a}</p>
        </div>
      )}
    </div>
  )
}

// ── 예시 컴포넌트 ─────────────────────────────────────────────────────────────
function ExampleCard({ ex, idx }: { ex: { q: string; a: string; detail: string }; idx: number }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      <div className="p-3">
        <p className="text-xs font-bold text-slate-400 mb-1">예시 {idx + 1}</p>
        <p className="text-sm text-slate-800 whitespace-pre-line">{ex.q}</p>
        <div className="mt-2 bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
          <p className="text-sm font-bold text-primary-700">💡 {ex.a}</p>
        </div>
      </div>
      {ex.detail && (
        <button
          onClick={() => setOpen(!open)}
          className="w-full text-left px-3 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 hover:bg-slate-100 transition-colors flex items-center gap-1"
        >
          <ArrowRight size={10} className={`transition-transform ${open ? 'rotate-90' : ''}`} />
          {open ? '풀이 숨기기' : '상세 풀이 보기'}
        </button>
      )}
      {open && ex.detail && (
        <div className="px-3 pb-3 bg-slate-50 text-xs text-slate-600 leading-relaxed">
          {ex.detail}
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
const UNIT_ID = 'F2-1'

export default function LearnPage({ defaultStepId }: { defaultStepId?: string | null }) {
  const [selectedId, setSelectedId] = useState<string | null>(defaultStepId ?? null)
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const getApprovedByTag   = useVideoStore(s => s.getApprovedByTag)
  const getRecruitingByTag = useVideoStore(s => s.getRecruitingByTag)
  const { completedCount, steps: stepProgress, pct } = useLearnProgress(UNIT_ID)
  const { completeStep, init } = useProgressStore(s => ({ completeStep: s.completeStep, init: s.init }))

  useEffect(() => { init('student_001') }, [])

  // 외부에서 defaultStepId가 변경되면 해당 단계로 이동 (오답→학습 연결)
  useEffect(() => {
    if (defaultStepId) {
      setSelectedId(defaultStepId)
      // 해당 단계 카드로 스크롤
      setTimeout(() => {
        const el = document.getElementById(`step-${defaultStepId}`)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
    }
  }, [defaultStepId])

  const selected = STEPS.find(s => s.id === selectedId) ?? null
  const selectedIdx = STEPS.findIndex(s => s.id === selectedId)

  const isStepCompleted = (id: string) =>
    stepProgress[id]?.status === 'completed' || stepProgress[id]?.status === 'mastered'
  const isStepPracticed = (id: string) => stepProgress[id]?.practiceAnswered ?? false

  const handleComplete = (stepId: string) => {
    const step = STEPS.find(s => s.id === stepId)
    completeStep(UNIT_ID, stepId, step?.title)
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <BookOpen size={24} className="text-blue-500" />
          학습 단계
        </h1>
        <p className="text-sm text-slate-500 mt-1">12단계 체계적 학습 경로 — 변수 이해 → 오답 분석</p>
      </div>

      {/* Progress Bar */}
      <div className="card mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-semibold text-slate-700">학습 진도</span>
          <span className="text-primary-600 font-bold">{completedCount} / 12 단계 완료</span>
        </div>
        <div className="bg-slate-100 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-primary-500 to-accent-500 h-3 rounded-full transition-all duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1.5">
          <span>시작</span>
          <span className="font-semibold text-primary-500">{pct}%</span>
          <span>완료</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Step List */}
        <div className="lg:col-span-1 space-y-2">
          {STEPS.map((step) => {
            const isActive = step.id === selectedId
            const completed = isStepCompleted(step.id)
            const practiced = isStepPracticed(step.id)
            const diffConf = DIFFICULTY_CONFIG[step.difficulty]

            return (
              <button
                key={step.id}
                id={`step-${step.id}`}
                onClick={() => setSelectedId(prev => prev === step.id ? null : step.id)}
                className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                  isActive
                    ? `${step.color.border} ${step.color.bg} shadow-md`
                    : completed
                      ? 'border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-lg ${
                    completed ? 'bg-emerald-500' : isActive ? step.color.btn + ' opacity-90' : 'bg-slate-100'
                  }`}>
                    {completed
                      ? <CheckCircle size={18} className="text-white" />
                      : <span>{step.icon}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-400">{step.id}</p>
                    <p className={`text-sm font-semibold truncate ${isActive ? step.color.accent : 'text-slate-700'}`}>
                      {step.title}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${diffConf.color}`}>
                        {diffConf.label}
                      </span>
                      {practiced && !completed && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-blue-100 text-blue-600">연습완료</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight
                    size={14}
                    className={`flex-shrink-0 transition-transform ${isActive ? 'rotate-90 ' + step.color.accent : 'text-slate-300'}`}
                  />
                </div>
              </button>
            )
          })}
        </div>

        {/* Step Detail */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="card flex flex-col items-center justify-center py-20 text-center">
              <div className="text-6xl mb-4">📚</div>
              <p className="text-lg font-bold text-slate-600 mb-1">학습 단계를 선택하세요</p>
              <p className="text-sm text-slate-400">왼쪽 목록에서 원하는 단계를 클릭하면<br />개념 설명과 예시, 연습 문제가 나타납니다.</p>
              {completedCount > 0 && (
                <div className="mt-4 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <p className="text-sm font-semibold text-emerald-700">
                    🎉 {completedCount}단계 완료! 계속 학습하세요.
                  </p>
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {STEPS.slice(0, 4).map(s => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 transition-colors text-slate-600"
                  >
                    {s.icon} {s.title}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header Card */}
              <div className={`rounded-2xl border-2 ${selected.color.border} ${selected.color.bg} p-5`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{selected.icon}</span>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selected.color.badge}`}>{selected.id}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DIFFICULTY_CONFIG[selected.difficulty].color}`}>
                          {DIFFICULTY_CONFIG[selected.difficulty].label}
                        </span>
                        {isStepCompleted(selected.id) && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center gap-1">
                            <CheckCircle size={10} /> 완료
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-black text-slate-800">{selected.title}</h2>
                      <p className="text-sm text-slate-500 mt-0.5">{selected.subtitle}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-white/70 rounded-xl">
                  <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">
                    <Target size={11} /> 학습 목표
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">{selected.objective}</p>
                </div>
                {selected.teaches.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selected.teaches.map(c => (
                      <span key={c} className={`text-xs px-2 py-0.5 rounded-full font-mono font-medium ${selected.color.badge}`}>
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Concept */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Lightbulb size={16} className="text-yellow-500" />
                    개념 설명
                  </h3>
                  {/* 파편 학습 — 영상 복습 버튼 (3단계 UI) */}
                  {(() => {
                    const approved   = getApprovedByTag(selected.id)
                    const recruiting = getRecruitingByTag(selected.id)
                    // ① approved 영상 있음 → 빨간 YouTube 버튼
                    if (approved.length > 0) {
                      return (
                        <button
                          onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-red-500 hover:bg-red-600 text-white shadow-sm hover:shadow-md"
                          title={`승인된 영상 ${approved.length}개 있음`}
                        >
                          <Youtube size={12} />
                          영상으로 학습 ({approved.length})
                        </button>
                      )
                    }
                    // ② recruiting 슬롯만 있음 → 보라색 "모집 중" 버튼
                    if (recruiting.length > 0) {
                      return (
                        <button
                          onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-violet-100 hover:bg-violet-200 text-violet-700 border border-violet-300 hover:border-violet-400"
                          title="영상 크리에이터 모집 중 — 클릭하면 지원 안내"
                        >
                          <Youtube size={12} />
                          🎬 영상 모집 중
                        </button>
                      )
                    }
                    // ③ 슬롯도 없음 → 회색 업로드 유도 버튼
                    return (
                      <button
                        onClick={() => setVideoModal({ stepId: selected.id, label: selected.title })}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-bold transition-all bg-slate-100 hover:bg-slate-200 text-slate-400 border border-slate-200"
                        title="영상 없음 — 업로드 신청하기"
                      >
                        <Youtube size={12} />
                        영상 학습
                      </button>
                    )
                  })()}
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <pre className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-sans">
                    {selected.concept}
                  </pre>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {selected.keyPoints.map(p => (
                    <span key={p} className="text-xs bg-primary-100 text-primary-700 px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                      <Star size={9} /> {p}
                    </span>
                  ))}
                </div>
              </div>

              {/* Examples */}
              <div className="card">
                <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                  <Calculator size={16} className="text-blue-500" />
                  예시 문제
                </h3>
                <div className="space-y-3">
                  {selected.examples.map((ex, i) => (
                    <ExampleCard key={i} ex={ex} idx={i} />
                  ))}
                </div>
              </div>

              {/* Practice — progressStore 연동 */}
              <PracticeBox
                practice={selected.practice}
                stepId={selected.id}
                unitId={UNIT_ID}
                alreadyAnswered={isStepPracticed(selected.id)}
                onAnswered={() => {}}
              />

              {/* Misconceptions */}
              {selected.misconceptions.length > 0 && (
                <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4">
                  <h3 className="font-bold text-red-700 text-sm mb-3 flex items-center gap-2">
                    <AlertTriangle size={14} /> 자주 하는 실수 (오개념 주의!)
                  </h3>
                  <div className="space-y-2">
                    {selected.misconceptions.map(m => (
                      <div key={m.code} className="flex items-start gap-2 bg-white rounded-lg px-3 py-2 border border-red-100">
                        <span className="text-xs font-black text-red-500 bg-red-100 px-1.5 py-0.5 rounded flex-shrink-0">{m.code}</span>
                        <span className="text-xs text-slate-700">{m.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── 단계 완료 버튼 ──────────────────────────────────────── */}
              {!isStepCompleted(selected.id) ? (
                <button
                  onClick={() => handleComplete(selected.id)}
                  className={`w-full py-3 rounded-2xl text-white font-black text-sm flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg ${selected.color.btn}`}
                >
                  <CheckCircle size={16} /> 이 단계 학습 완료!
                </button>
              ) : (
                <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-3 flex items-center gap-3">
                  <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-black text-emerald-700">완료된 단계입니다 🎉</p>
                    <p className="text-xs text-emerald-500">완료일: {stepProgress[selected.id]?.completedAt?.slice(0,10) ?? ''}</p>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center pt-2">
                {selectedIdx > 0 ? (
                  <button
                    onClick={() => setSelectedId(STEPS[selectedIdx - 1].id)}
                    className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
                  >
                    ← 이전 단계
                  </button>
                ) : <span />}
                {selectedIdx < STEPS.length - 1 ? (
                  <button
                    onClick={() => setSelectedId(STEPS[selectedIdx + 1].id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all ${selected.color.btn}`}
                  >
                    다음 단계 <ArrowRight size={14} />
                  </button>
                ) : (
                  <span className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle size={14} /> 전 과정 완료!
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 파편 학습 영상 모달 */}
      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          viewerId="student_001"
          onClose={() => setVideoModal(null)}
          onCompleted={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
